import { useEffect, useRef } from 'react';
import { personalInfo, socialLinks } from '../../../mocks/portfolio';

const HeroSection = () => {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    const el = heroRef.current;
    if (el) {
      el.querySelectorAll('[data-animate]').forEach((child) => observer.observe(child));
    }
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      ref={heroRef}
      className="min-h-screen flex items-center pt-[70px] section-padding overflow-hidden relative"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-orange-100/60 dark:bg-orange-900/10 blur-3xl" />
        <div className="absolute bottom-20 -left-20 w-72 h-72 rounded-full bg-rose-100/50 dark:bg-rose-900/10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center relative z-10">
        {/* Left: Text Content */}
        <div className="flex flex-col">
          <div
            data-animate
            className="opacity-0 translate-y-8 transition-all duration-700 delay-100"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-xs font-medium mb-6 whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
              {personalInfo.available ? '开放合作机会' : '暂不接受合作'}
              <span className="mx-1 text-orange-300 dark:text-orange-700">·</span>
              {personalInfo.title}
            </span>
          </div>

          <div
            data-animate
            className="opacity-0 translate-y-8 transition-all duration-700 delay-200"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4">
              <span className="text-gray-900 dark:text-white">Building</span>
              <br />
              <span className="text-gradient font-serif italic">Intelligent</span>
              <br />
              <span className="text-gray-900 dark:text-white">Experiences</span>
            </h1>
          </div>

          <div
            data-animate
            className="opacity-0 translate-y-8 transition-all duration-700 delay-300"
          >
            <p className="text-base md:text-lg text-gray-500 dark:text-gray-400 leading-relaxed max-w-lg mb-8">
              {personalInfo.tagline}
            </p>
          </div>

          <div
            data-animate
            className="opacity-0 translate-y-8 transition-all duration-700 delay-[400ms] flex flex-wrap gap-4 mb-10"
          >
            <button
              onClick={() => scrollTo('projects')}
              className="flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-semibold text-sm hover:bg-orange-500 dark:hover:bg-orange-500 dark:hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              查看项目案例
              <i className="ri-arrow-right-line" />
            </button>
            <button
              onClick={() => scrollTo('contact')}
              className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-sm hover:border-orange-500 hover:text-orange-500 dark:hover:border-orange-400 dark:hover:text-orange-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              联系我
              <i className="ri-mail-line" />
            </button>
          </div>

          <div
            data-animate
            className="opacity-0 translate-y-8 transition-all duration-700 delay-[500ms] flex items-center gap-4"
          >
            <span className="text-xs text-gray-400 whitespace-nowrap">关注我</span>
            <div className="w-12 h-px bg-gray-200 dark:bg-gray-700" />
            <div className="flex items-center gap-2">
              {socialLinks.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label={s.name}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-orange-500 hover:text-orange-500 hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div
            data-animate
            className="opacity-0 translate-y-8 transition-all duration-700 delay-[600ms] grid grid-cols-3 gap-6 mt-12 pt-12 border-t border-gray-100 dark:border-gray-800"
          >
            {[
              { value: personalInfo.experience, label: '产品经验' },
              { value: personalInfo.projects, label: '落地项目' },
              { value: personalInfo.users, label: '服务用户' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Visual */}
        <div
          data-animate
          className="opacity-0 translate-y-8 transition-all duration-700 delay-[300ms] flex justify-center lg:justify-end"
        >
          <div className="relative">
            {/* Main image container - AI illustration */}
            <div className="w-72 h-[400px] md:w-[380px] md:h-[480px] rounded-3xl overflow-hidden relative shadow-2xl shadow-orange-100/60 dark:shadow-orange-900/20">
              <img
                src={personalInfo.heroImage}
                alt="AI 智能体技术可视化"
                title="AI Product Manager - 智能体产品设计"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              {/* Avatar overlay at bottom */}
              <div className="absolute bottom-5 left-5 flex items-center gap-3 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-2xl px-4 py-3 border border-white/50 dark:border-gray-700/50">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-orange-300">
                  <img src={personalInfo.avatar} alt={personalInfo.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white">{personalInfo.name}</div>
                  <div className="text-xs text-orange-500 font-medium">{personalInfo.title}</div>
                </div>
              </div>
            </div>

            {/* Floating card: Role */}
            <div className="absolute -bottom-6 -right-6 bg-white dark:bg-gray-800 rounded-2xl px-4 py-3 shadow-lg border border-gray-100 dark:border-gray-700 animate-float">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-900/30">
                  <i className="ri-robot-line text-orange-500 text-lg" />
                </div>
                <div>
                  <div className="text-xs text-gray-400">专注领域</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">AI 产品设计</div>
                </div>
              </div>
            </div>

            {/* Floating card: Projects */}
            <div className="absolute -top-4 -right-8 bg-white dark:bg-gray-800 rounded-2xl px-4 py-3 shadow-lg border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-green-400" />
                <span className="text-xs text-gray-400 whitespace-nowrap">已落地项目</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{personalInfo.projects}</div>
            </div>

            {/* Decorative circles */}
            <div className="absolute -z-10 top-8 left-8 w-full h-full rounded-3xl border-2 border-dashed border-orange-200 dark:border-orange-800/50" />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-xs text-gray-400">向下滚动</span>
        <i className="ri-arrow-down-line text-gray-400" />
      </div>
    </section>
  );
};

export default HeroSection;
